import { useMemo, useRef, useState } from 'react'

import { logHeardError } from '@/utils/errorHelpers'

import {
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS,
  type AttachmentCallbacks,
  type AttachmentItem,
  type AttachmentRef,
  type AttachmentUploadError,
  type AttachmentUploader,
} from '../lib/attachments.types'

const isAttachmentUploadError = (
  error: unknown
): error is AttachmentUploadError =>
  error instanceof Error && 'percent' in error && 'aborted' in error

const oversizeMessage = `This file is over the ${
  MAX_ATTACHMENT_BYTES / (1024 * 1024)
} MB limit.`

const hasUploadedRef = (
  item: AttachmentItem
): item is AttachmentItem & { ref: AttachmentRef } =>
  item.status === 'uploaded' && item.ref !== null

export const useAttachments = (
  uploader: AttachmentUploader,
  callbacks?: AttachmentCallbacks
) => {
  const [items, setItems] = useState<AttachmentItem[]>([])
  const itemsRef = useRef<AttachmentItem[]>(items)
  const controllers = useRef(new Map<string, AbortController>())
  const callbacksRef = useRef(callbacks)
  callbacksRef.current = callbacks

  const updateItems = (
    updater: AttachmentItem[] | ((prev: AttachmentItem[]) => AttachmentItem[])
  ): void => {
    setItems((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      itemsRef.current = next
      return next
    })
  }

  const startUpload = (id: string, file: File): void => {
    const controller = new AbortController()
    controllers.current.set(id, controller)

    uploader
      .upload(
        file,
        (percent) => {
          updateItems((prev) =>
            prev.map((item) => (item.id === id ? { ...item, percent } : item))
          )
        },
        controller.signal
      )
      .then((ref) => {
        controllers.current.delete(id)
        updateItems((prev) =>
          prev.map((item) =>
            item.id === id
              ? { ...item, status: 'uploaded', ref, percent: 100, error: null }
              : item
          )
        )
      })
      .catch((error: unknown) => {
        controllers.current.delete(id)
        const uploadError = isAttachmentUploadError(error) ? error : null

        if (uploadError?.aborted) {
          updateItems((prev) => prev.filter((item) => item.id !== id))
          return
        }

        const percent = uploadError?.percent ?? 0
        const message =
          error instanceof Error ? error.message : 'Upload failed.'

        callbacksRef.current?.onUploadFailed?.({ reason: message, percent })
        updateItems((prev) =>
          prev.map((item) =>
            item.id === id
              ? { ...item, status: 'failed', percent, error: message }
              : item
          )
        )
      })
  }

  const add = (files: File[]): void => {
    const remaining = Math.max(0, MAX_ATTACHMENTS - itemsRef.current.length)
    const toProcess = files.slice(0, remaining)

    const newItems: AttachmentItem[] = toProcess.map((file) => {
      const oversize = file.size > MAX_ATTACHMENT_BYTES
      return {
        id: crypto.randomUUID(),
        file,
        fileName: file.name,
        size: file.size,
        status: oversize ? 'rejected' : 'uploading',
        percent: 0,
        error: oversize ? oversizeMessage : null,
        ref: null,
      }
    })

    updateItems((prev) => [...prev, ...newItems])

    newItems.forEach((item) => {
      if (item.status === 'uploading') {
        callbacksRef.current?.onAdded?.({
          size: item.size,
          contentType: item.file.type,
        })
        startUpload(item.id, item.file)
      }
    })
  }

  const retry = (id: string): void => {
    const item = itemsRef.current.find((candidate) => candidate.id === id)
    if (!item) {
      return
    }

    updateItems((prev) =>
      prev.map((candidate) =>
        candidate.id === id
          ? { ...candidate, status: 'uploading', percent: 0, error: null }
          : candidate
      )
    )
    startUpload(id, item.file)
  }

  const remove = (id: string): void => {
    const item = itemsRef.current.find((candidate) => candidate.id === id)
    if (!item) {
      return
    }

    controllers.current.get(id)?.abort()
    controllers.current.delete(id)
    updateItems((prev) => prev.filter((candidate) => candidate.id !== id))
    callbacksRef.current?.onRemoved?.()

    if (item.ref) {
      void uploader.remove(item.ref).catch((error) => {
        logHeardError(error)
      })
    }
  }

  const cancel = (id: string): void => {
    const item = itemsRef.current.find((candidate) => candidate.id === id)
    if (item?.ref) {
      remove(id)
      return
    }
    controllers.current.get(id)?.abort()
  }

  const clear = (): void => {
    const toRelease = itemsRef.current.filter(hasUploadedRef)

    itemsRef.current.forEach((item) => {
      controllers.current.get(item.id)?.abort()
    })
    controllers.current.clear()
    updateItems([])

    toRelease.forEach((item) => {
      void uploader.remove(item.ref).catch((error) => {
        logHeardError(error)
      })
    })
  }

  const refs = useMemo(
    () => items.filter(hasUploadedRef).map((item) => item.ref),
    [items]
  )
  const uploadedFiles = useMemo(
    () => items.filter(hasUploadedRef).map((item) => item.file),
    [items]
  )
  const isUploading = items.some((item) => item.status === 'uploading')
  const isFull = items.length >= MAX_ATTACHMENTS

  return {
    items,
    add,
    retry,
    cancel,
    remove,
    clear,
    refs,
    uploadedFiles,
    isUploading,
    isFull,
  }
}
